import { BaseProduct, ProductBuilderMachineContext } from '../types';
import { productHasVariantWithCellDesign } from './cellDesign';
import { productHasColour, productHasDayColour } from './colour';
import { productHasDriveControl } from './driveControl';
import { isCurtain, isSheer } from './isCurtain';
import { isHoneycombVertical } from './isHoneycombVertical';
import { isRollerDayNight } from './isRollerDayNight';
import { isRollerEssentials } from './isRollerEssentials';
import { isRollerSingle } from './isRollerSingle';
import { isRomanBlinds } from './isRomanBlinds';
import { isShutters } from './isShutters';
import { isVenetianBlinds } from './isVenetianBlinds';
import { productHasVariantWithLining } from './lining';
import { productHasVariantWithMaterialType } from './material';
import { productHasVariantWithMaterialTypeDayNight } from './materialTypeDayNight';
import { productHasStackSide } from './stackSide';

type GetBaseProduct = (
  context: ProductBuilderMachineContext,
) => BaseProduct | void;

const getBaseProductDefault: GetBaseProduct = ({
  products,
  custom: {
    colour,
    colourNight,
    colourDay,
    driveControl,
    materialType,
    cellDesign,
    stackSide,
    dayColour,
  },
}) => {
  const cellDesignFilter =
    typeof cellDesign !== 'undefined'
      ? productHasVariantWithCellDesign(cellDesign)
      : (product: BaseProduct) => true;
  const driveControlFilter =
    typeof driveControl !== 'undefined'
      ? productHasDriveControl(driveControl)
      : (product: BaseProduct) => true;

  const stackSideFilter =
    typeof stackSide !== 'undefined'
      ? productHasStackSide(stackSide)
      : (product: BaseProduct) => true;

  const colourFilter = (product: BaseProduct) =>
    typeof colour !== 'undefined' || typeof colourDay !== 'undefined'
      ? productHasColour(colour)(product) ||
        productHasColour(colourDay)(product)
      : true;

  const colourNightFilter = (product: BaseProduct) =>
    typeof colourNight !== 'undefined'
      ? productHasColour(colourNight)(product)
      : true;

  const daycolorFilter =
    typeof dayColour !== 'undefined'
      ? productHasDayColour(dayColour)
      : (product: BaseProduct) => true;

  return products
    .filter(productHasVariantWithMaterialType(materialType))
    .filter(colourFilter)
    .filter(colourNightFilter)
    .filter(daycolorFilter)
    .filter(cellDesignFilter)
    .filter(stackSideFilter)
    .find(driveControlFilter);
};

const getBaseProductRollerSingle: GetBaseProduct = ({
  products,
  custom: { colour, driveControl, materialType },
}) => {
  const driveControlFilter =
    typeof driveControl !== 'undefined'
      ? productHasDriveControl(driveControl)
      : (product: BaseProduct) => true;
  const colourFilter = (product: BaseProduct) =>
    typeof colour !== 'undefined' ? productHasColour(colour)(product) : true;
  const materialFilter = (product: BaseProduct) =>
    typeof materialType !== 'undefined'
      ? productHasVariantWithMaterialType(materialType)(product)
      : true;

  // There was a requirement from Veneta to start with these colours because they have the largest sizes
  products.sort((a, b) => {
    return a.title.includes('Glamour-Broken White') ||
      a.title.includes('Emery-Daylight') ||
      a.title.includes('Windsong-Soft White')
      ? -1
      : b.title.includes('Glamour-Broken White') ||
        b.title.includes('Emery-Daylight') ||
        b.title.includes('Windsong-Soft White')
      ? 1
      : 0;
  });

  return products
    .filter(materialFilter)
    .filter(colourFilter)
    .find(driveControlFilter);
};

const getBaseProductRollerDayNight: GetBaseProduct = ({
  products,
  custom: { colourDay, driveControl, materialTypeDay, materialTypeNight },
}) => {
  const driveControlFilter =
    typeof driveControl !== 'undefined'
      ? productHasDriveControl(driveControl)
      : (product: BaseProduct) => true;
  const colourFilter = (product: BaseProduct) =>
    typeof colourDay !== 'undefined'
      ? productHasColour(colourDay)(product)
      : true;
  const materialFilter = (product: BaseProduct) =>
    typeof materialTypeDay !== 'undefined'
      ? productHasVariantWithMaterialType(materialTypeDay)(product)
      : true;

  // There was a requirement from Veneta to start with these colours because they have the largest sizes
  products.sort((a, b) => {
    return a.title.includes('Glamour-Broken White') ||
      a.title.includes('Emery-Daylight') ||
      a.title.includes('Windsong-Soft White')
      ? -1
      : b.title.includes('Glamour-Broken White') ||
        b.title.includes('Emery-Daylight') ||
        b.title.includes('Windsong-Soft White')
      ? 1
      : 0;
  });
  return products
    .filter(materialFilter)
    .filter(colourFilter)
    .find(driveControlFilter);
};

export const getBaseProductRollerNight: GetBaseProduct = ({
  products,
  custom: { colourNight, driveControl, materialTypeNight },
}) => {
  const driveControlFilter =
    typeof driveControl !== 'undefined'
      ? productHasDriveControl(driveControl)
      : (product: BaseProduct) => true;
  const colourFilter = (product: BaseProduct) =>
    typeof colourNight !== 'undefined'
      ? productHasColour(colourNight)(product)
      : true;
  const materialFilter = (product: BaseProduct) =>
    typeof materialTypeNight !== 'undefined'
      ? productHasVariantWithMaterialType(materialTypeNight)(product)
      : true;
  return products
    .filter(materialFilter)
    .filter(colourFilter)
    .find(driveControlFilter);
};

const getBaseProductShutters: GetBaseProduct = ({
  products,
  custom: { colour },
}) => {
  return products.find(product => productHasColour(colour)(product));
};

const getBaseProductVenetianBlinds: GetBaseProduct = ({
  products,
  custom: { colour, driveControl },
}) => {
  const driveControlFilter =
    typeof driveControl !== 'undefined'
      ? productHasDriveControl(driveControl)
      : (product: BaseProduct) => true;

  const colourFilter = (product: BaseProduct) =>
    typeof colour !== 'undefined' ? productHasColour(colour)(product) : true;

  return products.filter(colourFilter).find(driveControlFilter);
};

const getBaseProductRomanBlinds: GetBaseProduct = ({
  products,
  custom: { colour, driveControl, lining },
}) => {
  
  const driveControlFilter =
    typeof driveControl !== 'undefined'
      ? productHasDriveControl(driveControl)
      : (product: BaseProduct) => true;

  const colourFilter = (product: BaseProduct) =>
    typeof colour !== 'undefined' ? productHasColour(colour)(product) : true;

  const liningFilter = (product: BaseProduct) =>
    typeof lining !== 'undefined'
      ? productHasVariantWithLining(lining)(product)
      : true;
      
  const afterColourFilter = products.filter(colourFilter);
  const afterLiningFilter = afterColourFilter.filter(liningFilter);
  const result = afterLiningFilter.find(driveControlFilter);
  

  
  return result;
};

const getBaseProductCurtain: GetBaseProduct = ({
  products,
  custom: { colour },
}) => {
  return products.find(product => productHasColour(colour)(product));
};

export const getBaseProduct: GetBaseProduct = context => {
  if (isRollerSingle(context)) return getBaseProductRollerSingle(context);
  if (isRollerEssentials(context)) return getBaseProductRollerSingle(context);
  if (isRollerDayNight(context)) return getBaseProductRollerDayNight(context);
  if (isShutters(context)) return getBaseProductShutters(context);
  if (isVenetianBlinds(context)) return getBaseProductVenetianBlinds(context);
  if (isRomanBlinds(context)) return getBaseProductRomanBlinds(context);
  if (isCurtain(context)) return getBaseProductCurtain(context);
  if (isSheer(context)) return getBaseProductCurtain(context);
  return getBaseProductDefault(context);
};
